import { useMutation } from "@apollo/client";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LabelOff from "@mui/icons-material/LabelOff";
import LabelOutlined from "@mui/icons-material/LabelOutlined";
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCallback, useState } from "react";
import {
  WASTE_LINES_UNIFIED_BULK_UPDATE_MUTATION,
  WASTE_LINES_UNIFIED_QUERY,
} from "./api";
import {
  ManageWasteLineTagsDialog,
  ManageWasteLineTagsMenuItem,
} from "./ManageWasteLineTags";

type WasteLineUnifiedRow = {
  id: string;
  tags?: { id: string; name: string }[];
};

export const StaffActionsMenu = function ({
  selectedRows = [],
  onCompleted,
}: {
  selectedRows: WasteLineUnifiedRow[];
  onCompleted: () => void;
}) {
  const alerts = useAlerts();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [manageTagsDialogOpen, setManageTagsDialogOpen] = useState(false);

  const selectedWasteLineIds = selectedRows.map((row) => row.id);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleOpenManageTags = useCallback(() => {
    setManageTagsDialogOpen(true);
    handleClose();
  }, [handleClose]);

  const handleCloseManageTags = useCallback(() => {
    setManageTagsDialogOpen(false);
    onCompleted();
  }, [onCompleted]);

  // Transform selected rows to include tags as string arrays
  const transformedRows = selectedRows.map((row) => ({
    id: row.id,
    tags: row.tags?.map((tag) => tag.name) || [],
  }));

  const [bulkMutate, { loading }] = useMutation(
    WASTE_LINES_UNIFIED_BULK_UPDATE_MUTATION,
    {
      refetchQueries: [WASTE_LINES_UNIFIED_QUERY],
    }
  );

  const handleMarkNotReportable = useCallback(async () => {
    try {
      const result = await bulkMutate({
        variables: {
          input: { ids: selectedWasteLineIds, willNotReport: true },
        },
      });
      alerts.success(
        `Marked ${result.data?.wasteLinesUnifiedBulkUpdateWillNotReport} of ${selectedWasteLineIds.length} waste lines as not reportable`
      );
      onCompleted();
      handleClose();
    } catch (err) {
      alerts.error("Failed to mark waste lines as not reportable", err);
    }
  }, [bulkMutate, selectedWasteLineIds, alerts, onCompleted, handleClose]);

  const handleMarkReportable = useCallback(async () => {
    try {
      const result = await bulkMutate({
        variables: {
          input: { ids: selectedWasteLineIds, willNotReport: false },
        },
      });
      alerts.success(
        `Marked ${result.data?.wasteLinesUnifiedBulkUpdateWillNotReport} of ${selectedWasteLineIds.length} waste lines as reportable`
      );
      onCompleted();
      handleClose();
    } catch (err) {
      alerts.error("Failed to mark waste lines as reportable", err);
    }
  }, [bulkMutate, selectedWasteLineIds, alerts, onCompleted, handleClose]);

  const buttonId = "staff-actions";

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        color="warning"
        loading={loading}
        disabled={selectedWasteLineIds.length === 0}
      >
        Staff
      </Button>
      <Menu
        id={buttonId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": buttonId,
            dense: true,
          },
        }}
      >
        <MenuItem
          key="mark-not-reportable"
          onClick={handleMarkNotReportable}
          disabled={loading}
        >
          <ListItemIcon>
            <LabelOff fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark not reportable</ListItemText>
        </MenuItem>
        <MenuItem
          key="mark reportable"
          onClick={handleMarkReportable}
          disabled={loading}
        >
          <ListItemIcon>
            <LabelOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark reportable</ListItemText>
        </MenuItem>
        <Divider />
        <ManageWasteLineTagsMenuItem
          selectedWasteLineIds={selectedWasteLineIds}
          onClick={handleOpenManageTags}
        />
      </Menu>
      {manageTagsDialogOpen && (
        <ManageWasteLineTagsDialog
          selectedRows={transformedRows}
          dialogOpen={manageTagsDialogOpen}
          onClose={handleCloseManageTags}
        />
      )}
    </>
  );
};
